import * as React from 'react';
import clsx from 'clsx';
import { MessageList } from '@/components/chat/MessageList';
import { ChatInput } from '@/components/chat';
import { ChatHeaderContainer as ChatHeader } from '@/components/chat/ChatHeader';
import { useChat } from '@/hooks/useChat';
import type { Echo, Message } from '@/types';
import type { EchoKey } from '@/utils/constants';
import type { EchoInfoEntry } from '@/components/chat/MessageList';
import waveElementChat from '@/assets/images/svg/wave_element_chat.svg';
import BackgroundBubbles from '@/components/common/BackgroundBubbles';
import { useChatContext } from '@/components/layout/ChatLayoutContext';
import { SuggestedQuestions } from '@/features/my-echo/components/suggested-questions/SuggestedQuestions';
import { findMessageWithSuggestions } from '@/features/my-echo/components/suggested-questions/utils';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/context/ToastContext';
import { userService } from '@/services/userService';
import { analyticsService } from '@/services/analyticsService';
import { HeaderVideoProvider } from '@/context/HeaderVideoContext';
import { useCreditFeedbackPopover } from '@/hooks/useCreditFeedbackPopover';
import { CreditFeedbackPopover } from '@/components/feedback/CreditFeedbackPopover';

export interface GroupChatState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  sendAudioMessage: (blob: Blob, waveform: number[], duration: number) => Promise<void>;
  clearConversation: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => void;
  isSending: boolean;
  echoInfoMap: Record<string, EchoInfoEntry>;
}

interface ChatViewProps {
  echo: Echo;
  echoKey: EchoKey;
  onOpenSidebar?: () => void;
  headerContent?:
    | React.ReactNode
    | ((sendMessage: (message: string) => Promise<void>, isLoading: boolean) => React.ReactNode);
  onSendMessage?: (message: string) => void;
  /** When provided, ChatView uses this external state instead of useChat (for group chats). */
  groupChat?: GroupChatState;
  /** Reduces header padding and text size for embedded contexts (e.g. My Echo test chat). */
  compact?: boolean;
}

/** Primary chat interface for a single echo conversation with streaming, audio, and suggested questions. */
export const ChatView: React.FC<ChatViewProps> = ({
  echo,
  echoKey,
  onOpenSidebar,
  headerContent,
  onSendMessage,
  groupChat,
  compact = false,
}) => {
  const { isTransitioning } = useChatContext();
  const { user, updateUser, refreshProfile } = useAuth();
  const { addToast } = useToast();
  const ttsProvider = 'elevenlabs';

  const isAudioEnabled = user?.audio_enabled ?? false;
  const credits = user?.credits ?? 0;
  const lifebarMax = user?.lifebar_max || credits;

  const creditFeedback = useCreditFeedbackPopover({
    credits,
    lifebarMax,
    enabled: !groupChat && !!user,
  });

  const handleCreditFeedbackSubmit = React.useCallback(
    async (answers: Record<string, string>) => {
      const result = await creditFeedback.submit(answers);
      if (!result) return;

      const parts: string[] = [];
      if (result.credits_granted > 0) {
        parts.push(`+${result.credits_granted} credits`);
      }
      if (result.discount_percent_granted) {
        parts.push(`${result.discount_percent_granted}% off your next purchase`);
      }

      addToast({
        title: 'Thanks for your feedback!',
        subtitle: parts.length > 0 ? parts.join(' · ') : undefined,
        type: 'success',
      });
      await refreshProfile().catch(() => undefined);
    },
    [creditFeedback, addToast, refreshProfile]
  );

  const videoStorageKey = `video-enabled:${echo.id}`;
  const [isVideoEnabled, setIsVideoEnabled] = React.useState(() => {
    try {
      return sessionStorage.getItem(videoStorageKey) === 'true';
    } catch {
      return false;
    }
  });

  const handleToggleVideo = React.useCallback(() => {
    setIsVideoEnabled((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(videoStorageKey, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, [videoStorageKey]);

  const handleToggleAudio = async () => {
    if (!user) return;

    const newValue = !isAudioEnabled;
    updateUser({ ...user, audio_enabled: newValue });

    try {
      await userService.updateMe({ audio_enabled: newValue });
    } catch {
      updateUser({ ...user, audio_enabled: !newValue });
      addToast({
        title: 'Update Failed',
        subtitle: 'Could not save audio preference.',
        type: 'error',
      });
    }
  };

  const [bubbleCount, setBubbleCount] = React.useState(20);

  React.useEffect(() => {
    const updateResources = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setBubbleCount(15);
      } else if (width < 1280) {
        setBubbleCount(35);
      } else {
        setBubbleCount(80);
      }
    };
    updateResources();
    let timeout: number | undefined;
    const handleResize = () => {
      if (timeout) clearTimeout(timeout);
      timeout = window.setTimeout(updateResources, 300);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeout) clearTimeout(timeout);
    };
  }, []);

  const echoChat = useChat({
    echoKey: groupChat ? ('' as EchoKey) : echoKey,
    ttsProvider,
    generateAudio: isAudioEnabled,
  });

  // Select data source: group chat state or regular echo chat
  const messages = groupChat?.messages ?? echoChat.messages;
  const isLoading = groupChat ? groupChat.isSending : echoChat.isLoading;
  const hasMore = groupChat?.hasMore ?? echoChat.hasMore;
  const clearConversation = groupChat?.clearConversation ?? echoChat.clearConversation;
  const loadMore = groupChat?.loadMore ?? echoChat.loadMore;
  const isInitialLoading = groupChat ? groupChat.isLoading : echoChat.isLoading;

  const handleSendAudio = (blob: Blob, waveform: number[], duration: number) => {
    if (isLoading) return;
    if (groupChat) {
      groupChat.sendAudioMessage(blob, waveform, duration);
      return;
    }
    echoChat.sendAudioMessage(blob, waveform, duration, isAudioEnabled);
  };

  const handleSend = async (msg: string) => {
    if (isLoading) return;

    if (onSendMessage) {
      onSendMessage(msg);
    }

    if (groupChat) {
      await groupChat.sendMessage(msg);
    } else {
      await echoChat.sendMessage(msg, {
        generateAudio: isAudioEnabled,
        generateVideo: isVideoEnabled && (echo.videoMessagesEnabled ?? false),
      });
    }
  };

  const resolvedHeaderContent =
    typeof headerContent === 'function' ? headerContent(handleSend, isLoading) : headerContent;

  const lastAiMessage = findMessageWithSuggestions(messages);
  const suggestedQuestions = lastAiMessage?.suggested_questions ?? [];

  /** Tracks the suggestion click for CTR analytics, then sends the question as a message. */
  const handleQuestionClick = (questionText: string) => {
    if (lastAiMessage?.id) {
      const questionIndex = suggestedQuestions.findIndex(
        (question) => question.text === questionText
      );
      analyticsService
        .trackSuggestionClick({
          message_id: lastAiMessage.id,
          echo_id: echo.id,
          question_index: Math.max(0, questionIndex),
        })
        .catch(() => undefined);
    }
    handleSend(questionText);
  };

  // Build mentions list from group chat echo info
  const mentionOptions = React.useMemo(() => {
    if (!groupChat?.echoInfoMap) return undefined;
    return Object.values(groupChat.echoInfoMap).map((e) => ({
      id: e.id,
      name: e.name,
      ...(e.avatarUrl !== undefined ? { avatarUrl: e.avatarUrl } : {}),
    }));
  }, [groupChat?.echoInfoMap]);

  return (
    <HeaderVideoProvider>
      <div className='relative flex size-full flex-col overflow-hidden bg-chat-bg-gradient'>
        <div className='pointer-events-none absolute inset-0 z-0 select-none overflow-hidden'>
          <div
            className={clsx(
              'transition-opacity duration-200',
              isTransitioning ? 'opacity-0' : 'opacity-100'
            )}
          >
            <BackgroundBubbles count={bubbleCount} />
          </div>

          <div
            className={clsx(
              'absolute bottom-0 left-1/2 hidden h-auto w-full md:block md:w-[70%]',
              'opacity-80',
              '-translate-x-1/2 translate-y-[60%]'
            )}
          >
            <img
              src={waveElementChat}
              alt=''
              className={clsx('size-full', !isTransitioning && 'animate-spin-slow-120')}
            />
          </div>
        </div>

        <ChatHeader
          echo={echo}
          onClear={clearConversation}
          disabled={isLoading}
          {...(onOpenSidebar ? { onOpenSidebar } : {})}
          isTransitioning={isTransitioning}
          isAudioEnabled={isAudioEnabled}
          onToggleAudio={handleToggleAudio}
          isVideoEnabled={isVideoEnabled}
          onToggleVideo={handleToggleVideo}
          compact={compact}
        >
          {resolvedHeaderContent}
        </ChatHeader>

        <div className='relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden'>
          <main
            className={clsx(
              'scrollbar-dark relative z-20 flex-1 overflow-y-auto px-4 py-2',
              compact ? 'xl:px-4' : 'xl:px-[48px]'
            )}
          >
            {isInitialLoading && messages.length === 0 ? (
              <div className='flex h-full flex-col items-center justify-center gap-4'>
                <div className='relative flex items-center justify-center'>
                  <div className='absolute size-12 animate-ping rounded-full bg-accent-300/20' />
                  <svg
                    className='size-10 animate-spin text-accent-300'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                </div>
                <p className='font-sans text-14 font-medium text-white/60'>
                  Reaching {echo.name}...
                </p>
              </div>
            ) : (
              <>
                {hasMore && (
                  <div className='mb-4 text-center'>
                    <button
                      onClick={loadMore}
                      disabled={isLoading}
                      className='rounded-md border border-white/20 bg-white/10 px-3 py-2 text-sm text-white shadow-sm hover:bg-white/20 disabled:opacity-50'
                    >
                      {isLoading ? 'Loading…' : 'Load more messages'}
                    </button>
                  </div>
                )}
                <MessageList
                  messages={messages}
                  isLoading={isLoading}
                  echoName={echo.name}
                  isTransitioning={isTransitioning}
                  echoId={echo.id}
                  isAudioEnabled={isAudioEnabled}
                  {...(groupChat?.echoInfoMap ? { echoInfoMap: groupChat.echoInfoMap } : {})}
                  videoMessagesEnabled={isVideoEnabled && (echo.videoMessagesEnabled ?? false)}
                />
              </>
            )}
          </main>

          <div className='relative z-20 w-full shrink-0'>
            {/* RESTORED: Suggested Questions Rendering */}
            {suggestedQuestions.length > 0 && (
              <div className='px-4 pb-2'>
                <SuggestedQuestions
                  questions={suggestedQuestions}
                  onQuestionSelect={handleQuestionClick}
                  disabled={isLoading}
                />
              </div>
            )}

            {creditFeedback.campaign ? (
              <CreditFeedbackPopover
                campaign={creditFeedback.campaign}
                isOpen={creditFeedback.isOpen}
                isSubmitting={creditFeedback.isSubmitting}
                onDismiss={creditFeedback.dismiss}
                onSubmit={handleCreditFeedbackSubmit}
              />
            ) : null}

            <ChatInput
              onSend={handleSend}
              onSendAudio={handleSendAudio}
              isLoading={isLoading}
              isTransitioning={isTransitioning}
              className={compact ? 'xl:px-4' : 'xl:px-[48px]'}
              {...(mentionOptions ? { mentions: mentionOptions } : {})}
            />
          </div>
        </div>
      </div>
    </HeaderVideoProvider>
  );
};

ChatView.displayName = 'ChatView';
